import { Component, EventEmitter, Output } from '@angular/core';
import { SubMenus } from '../../../interfaces/submenu.interface';
import { AuthService } from '../../../Services/Auth/auth.service';
import { UserProfileData } from '../../../interfaces/user-profile-data';
import { Router } from '@angular/router';
import { ThirdPartyNoticeComponent } from '@overtur-monorepo/phx-angular-material';
import { MatDialog } from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import { CanActivateService } from '../../../Services/CanActivate/can-activate.service';

@Component({
    selector: 'app-navbar',
    templateUrl: './navbar.component.html',
    styleUrls: ['./navbar.component.scss'],
    standalone: false
})
export class NavbarComponent {
  subMenus: SubMenus = {
    products: false,
    subscriptions: false,
    onboardingItems: false,
  };
  userProfile: UserProfileData | null = null;
  @Output() collapseChange = new EventEmitter<boolean>();
  isCollapsed: boolean = false;
  isMobileMenuOpen = false;
  showDashboardAndProducts: boolean = true;
  showDashboardAndProductsOvertur: boolean = true;
  showSATAdmin: boolean = false;
  isCompanyAdmin!: boolean;
  isZentraWholesaler!: boolean;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly canActivateService: CanActivateService
  ) {}

  /* istanbul ignore next */
  ngOnInit(): void {
    // base profile from AuthService (claims / token)
    this.authService.userProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe((profile) => {
        this.userProfile = profile ?? undefined;
        this.isCompanyAdmin = !!profile?.isCompanyAdmin;
        const roles: string[] = profile?.['http://allegion.com/claims/roles'] || [];
        this.showSATAdmin = roles.includes('SATAdmin');
      });

    // roles derived from backend permissions (e.g. ZentraIntegrator, ZentraWholesaler)
    this.authService
      .getAllRoles()
      .pipe(takeUntil(this.destroy$))
      .subscribe((roles: string[]) => {
        this.isZentraWholesaler = roles.includes('ZentraWholesaler');
      });

    // backend authoritative profile (may arrive later via CanActivateService)
    this.canActivateService.companyProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe((backendProfile) => {
        if (backendProfile) {
          // backend should override
          this.isCompanyAdmin = !!backendProfile.isCompanyAdmin;
          // merge for UI if you want the combined view
          this.userProfile = { ...(this.userProfile || {}), ...backendProfile };
        }
      });

    // permissions subscription (also use takeUntil)
    this.authService
      .getPermissions()
      .pipe(takeUntil(this.destroy$))
      .subscribe((permissions: string[]) => {
        if (permissions.includes('Create Trial Subscriptions')) {
          this.showDashboardAndProducts = false;
          this.showDashboardAndProductsOvertur = true;
        } else {
          this.showDashboardAndProducts = !permissions.includes(
            'Subscription Config View'
          );
          this.showDashboardAndProductsOvertur = false;
        }
      });
  }

  /* istanbul ignore next */
  toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
    this.collapseChange.emit(this.isCollapsed);
  }

  navigateToLanding(): void {
    this.router.navigateByUrl('/landing');
  }

  handleKeyDown(event: KeyboardEvent, menuName: string) {
    // For example, toggle submenu on 'Enter' key press
    if (event.key === 'Enter') {
      this.toggleSubMenu(menuName as keyof SubMenus);
    }
  }
  /* istanbul ignore next */
  openThirdPartyNotice() {
    this.dialog.open(ThirdPartyNoticeComponent, {
      width: '600px',
      height: 'auto',
      panelClass: 'third-party-notice-dialog',
      data: {
        title: 'Third Party Notice',
        content: 'This is a third party notice.',
      },
    });
  }
  /* istanbul ignore next */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /* istanbul ignore next */
  toggleMenu() {
    this.isCollapsed = !this.isCollapsed;
  }
  /* istanbul ignore next */
  toggleMobileMenu() {
    // this.isCollapsed = !this.isCollapsed;
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    console.log('Mobile menu open:', this.isMobileMenuOpen);
  }
  /* istanbul ignore next */
  closeMobileMenu() {
    this.isMobileMenuOpen = false;
    console.log('Closing mobile menu');
  }
  /* istanbul ignore next */
  toggleSubMenuAndClose(menu: keyof SubMenus): void {
    this.toggleSubMenu(menu);
    this.closeMobileMenu();
  }

  /* istanbul ignore next */
  toggleSubMenu(menu: keyof SubMenus): void {
    console.log('Toggle submenu called for:', menu);
    if (menu in this.subMenus) {
      this.subMenus[menu] = !this.subMenus[menu];
      if (this.subMenus[menu] && this.isCollapsed) {
        this.expandNavbar();
      }
    }
  }
  /* istanbul ignore next */
  expandNavbar(): void {
    if (this.isCollapsed) {
      this.isCollapsed = false;
      this.collapseChange.emit(this.isCollapsed);
      console.log('Navbar expanded:', this.isCollapsed);
    }
  }
  /* istanbul ignore next */
  logout() {
    console.log('Logging out...');
    this.authService.logout();
  }
  /* istanbul ignore next */
  shouldStackName(): boolean {
    const nameLengthThreshold = 15;
    return (
      (this.userProfile?.given_name?.length ?? 0) +
        (this.userProfile?.family_name?.length ?? 0) >
      nameLengthThreshold
    );
  }
}
